import React, { useState, useEffect } from 'react';
import { Play, X, Maximize2, ChevronLeft, ChevronRight } from 'lucide-react';

const GalleryPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [displayLimit, setDisplayLimit] = useState(12);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/gallery');
        const data = await response.json();
        // Sorter efter dato baseret på filnavn (f.eks. 20241031)
        const sortedData = data.sort((a, b) => {
          const titleA = a.title.da || '';
          const titleB = b.title.da || '';
          // Forsøg at trække dato ud (YYYYMMDD)
          const dateA = titleA.match(/\d{8}/)?.[0] || '0';
          const dateB = titleB.match(/\d{8}/)?.[0] || '0';
          
          if (dateA !== dateB) {
            return dateB.localeCompare(dateA); // Nyeste dato først
          }
          return titleB.localeCompare(titleA); // Alfabetisk hvis dato er ens
        });
        setItems(sortedData);
      } catch (error) {
        console.error('Error fetching gallery:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, []);

  const handleLoadMore = () => {
    setDisplayLimit(prev => prev + 12);
  };

  const handleOpenLightbox = (item, index) => {
    setSelectedItem(item);
    setSelectedIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseLightbox = () => {
    setSelectedItem(null);
    setSelectedIndex(-1);
    document.body.style.overflow = 'unset';
  };

  const handleNext = (e) => {
    e?.stopPropagation();
    const nextIndex = (selectedIndex + 1) % items.length;
    setSelectedIndex(nextIndex);
    setSelectedItem(items[nextIndex]);
  };

  const handlePrev = (e) => {
    e?.stopPropagation();
    const prevIndex = (selectedIndex - 1 + items.length) % items.length;
    setSelectedIndex(prevIndex);
    setSelectedItem(items[prevIndex]);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedItem) return;
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'Escape') handleCloseLightbox();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedItem, selectedIndex]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Vores Galleri</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Se resultaterne af vores arbejde. Vi går aldrig på kompromis med kvaliteten, uanset om det er en luksusvask eller en fuld keramisk coating.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.slice(0, displayLimit).map((item, index) => (
            <div
              key={item.id}
              className="group relative overflow-hidden rounded-2xl bg-white shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
              onClick={() => handleOpenLightbox(item, index)}
            >
              <div className="aspect-square overflow-hidden">
                {item.is_video ? (
                  <div className="relative h-full w-full">
                    <video
                      src={item.media[0]}
                      className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                      muted
                      playsInline
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
                      <div className="bg-white/90 p-3 rounded-full shadow-lg">
                        <Play className="w-6 h-6 text-blue-600 fill-current" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <img
                    src={item.media[0]}
                    alt={item.title.da}
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                )}
              </div>
              
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                <h3 className="text-white font-semibold text-lg">{item.title.da}</h3>
                <div className="flex items-center text-white/80 text-sm mt-2">
                  <Maximize2 className="w-4 h-4 mr-2" />
                  Klik for at se i fuld skærm
                </div>
              </div>
            </div>
          ))}
        </div>

        {displayLimit < items.length && (
          <div className="mt-12 text-center">
            <button
              onClick={handleLoadMore}
              className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Indlæs flere billeder
            </button>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {selectedItem && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4 sm:p-8"
          onClick={handleCloseLightbox}
        >
          <button
            onClick={handleCloseLightbox}
            className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors z-50 p-2"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Navigation Buttons */}
          <button
            onClick={handlePrev}
            className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors z-50 p-2 bg-white/10 hover:bg-white/20 rounded-full"
          >
            <ChevronLeft className="w-8 h-8 sm:w-10 sm:h-10" />
          </button>

          <button
            onClick={handleNext}
            className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors z-50 p-2 bg-white/10 hover:bg-white/20 rounded-full"
          >
            <ChevronRight className="w-8 h-8 sm:w-10 sm:h-10" />
          </button>
          
          <div 
            className="relative w-full max-w-5xl max-h-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            {selectedItem.is_video ? (
              <video
                key={selectedItem.id}
                src={selectedItem.media[0]}
                className="max-w-full max-h-[75vh] rounded-lg shadow-2xl"
                controls
                autoPlay
              />
            ) : (
              <img
                key={selectedItem.id}
                src={selectedItem.media[0]}
                alt={selectedItem.title.da}
                className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl"
              />
            )}
            <div className="mt-6 text-center">
              <h2 className="text-xl sm:text-2xl font-bold text-white">{selectedItem.title.da}</h2>
              <p className="text-white/60 mt-2">
                {selectedIndex + 1} af {items.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
